import { graphql, PageProps } from "gatsby";
import React, { FC, useEffect } from "react";

import { Footer } from "../../../components/footer";
import { HeaderMenuSection } from "../../../components/header-menu";
import { GlobalLayout } from "../../../components/layouts/global-layout";
import { mapLabelsByKey } from "../../../contexts/labels";
import { useTrackPageview } from "../../../hooks/use-track-pageview";
import { ContentGroup } from "../../../types/analytics";
import { NonEditorialPagesBreadcrumbs } from "../../../utils/analytics";
import { StandardPageContext } from "../../standard-page";
import { ProfilePageBody } from "./profile-page-body";

export type ProfilePageContext = Omit<StandardPageContext, "id">;

export interface SearchPageProps extends PageProps<Queries.ProfilePageQuery, ProfilePageContext> {}

const ProfilePage: FC<SearchPageProps> = ({
  data: {
    allContentfulHeaderMenu,
    allContentfulLabel,
    allContentfulRichTextLabel,
    allContentfulFooter,
  },
  pageContext: { locale, allPageNodes, localizedUrls },
}) => {
  const contentfulHeaderMenu = allContentfulHeaderMenu.nodes.find((x) => x.node_locale === locale);
  const contentfulFooter = allContentfulFooter.nodes.find((x) => x.node_locale === locale);

  const labels = mapLabelsByKey(
    Array.from(allContentfulLabel.nodes),
    (value) => value ?? ""
  ) as Record<string, string>;
  const richTextLabels = mapLabelsByKey(
    Array.from(allContentfulRichTextLabel.nodes),
    (value) => value?.raw ?? ""
  );

  const trackPageview = useTrackPageview();
  useEffect(() => {
    trackPageview({
      breadcrumbs: [
        {
          label: NonEditorialPagesBreadcrumbs.PersonalArea,
          defaultLocaleLabel: NonEditorialPagesBreadcrumbs.PersonalArea,
          url: "",
        },
        {
          label: NonEditorialPagesBreadcrumbs.Profile,
          defaultLocaleLabel: NonEditorialPagesBreadcrumbs.Profile,
          url: "",
        },
      ],
      content_group: ContentGroup.Other,
      locale,
    });
  }, [trackPageview, locale]);

  return (
    <GlobalLayout
      {...{ labels, richTextLabels, localizedUrls, locale }}
      pageNodes={allPageNodes ?? []}
      className="bg-neutral-light"
    >
      {contentfulHeaderMenu && (
        <HeaderMenuSection
          headerMenu={contentfulHeaderMenu}
          internalPagePath="/personal-area/profile"
          pageContentfulId={null}
        />
      )}
      <ProfilePageBody labels={labels} locale={locale} />
      {contentfulFooter && <Footer {...contentfulFooter} />}
    </GlobalLayout>
  );
};

export const query = graphql`
  query ProfilePage($locale: String) {
    allContentfulHeaderMenu(filter: { node_locale: { eq: $locale } }) {
      nodes {
        __typename
        ...HeaderMenuData
      }
    }
    allContentfulFooter(filter: { node_locale: { eq: $locale } }) {
      nodes {
        __typename
        ...FooterData
        node_locale
      }
    }
    allContentfulLabel(filter: { node_locale: { eq: $locale } }) {
      nodes {
        node_locale
        internalName
        value
      }
    }

    allContentfulRichTextLabel(filter: { node_locale: { eq: $locale } }) {
      nodes {
        node_locale
        internalName
        value {
          raw
        }
      }
    }
  }
`;

export default ProfilePage;
